// src/pages/problem-bank/ProblemDetail.tsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import {
  ArrowLeft,
  Download,
  CheckCircle,
  AlertTriangle,
  Clock,
  Building2,
  MapPin,
  Mail,
  Phone,
  FileText,
  Image,
  Video,
  TrendingUp,
  DollarSign,
  Users,
  Calendar,
  Award,
  BarChart3,
  Target,
  ExternalLink,
  Info,
  XCircle,
  Camera,
  FileSpreadsheet,
  Tag,
  CalendarDays,
  Hash,
  Gauge,
  ChevronRight,
  Code,
  ShieldCheck,
  Loader2,
  Trash2,
  UserCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const STATUS_COLORS: Record<string, string> = {
  lead: 'bg-gray-100 text-gray-700 border-gray-200',
  verified: 'bg-green-100 text-green-700 border-green-200',
  assigned: 'bg-purple-100 text-purple-700 border-purple-200',
  solved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  deleted: 'bg-red-100 text-red-700 border-red-200'
};

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low: 'bg-green-100 text-green-700 border-green-200'
};

const formatCurrency = (value: number): string => {
  if (!value) return 'KES 0';
  if (value >= 1_000_000_000) return `KES ${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `KES ${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `KES ${(value / 1_000).toFixed(1)}K`;
  return `KES ${value.toLocaleString()}`;
};

export default function ProblemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [problem, setProblem] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [deleteRequestDialogOpen, setDeleteRequestDialogOpen] = useState(false);
  const [deletionReason, setDeletionReason] = useState('');
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [existingRequest, setExistingRequest] = useState<any>(null);
  const [verifierName, setVerifierName] = useState<string | null>(null);
  
  // Priority score form state
  const [priorityForm, setPriorityForm] = useState({
    lossMagnitude: 3,
    anchorUrgency: 3,
    solutionFeasibility: 3,
    marketSize: 3,
    relationshipStrength: 3,
    notes: ''
  });

  // Check if user can verify (Problem Bank Analyst or Senior Analyst)
  const canVerify = () => {
    if (!user) return false;
    return user.role === 'senior_analyst' || user.analyst_type === 'problem_bank';
  };

  // Check if problem is in lead status (can be verified)
  const canBeVerified = () => {
    return problem?.status === 'lead';
  };

  // Check if user can request deletion (Problem Bank Analyst, not senior)
  const canRequestDeletion = () => {
    if (!user) return false;
    return user.analyst_type === 'problem_bank' && user.role !== 'senior_analyst' && problem?.status !== 'deleted';
  };

  // Check if problem is already deleted
  const isDeleted = () => {
    return problem?.status === 'deleted';
  };

  const hasPendingRequest = () => {
    return existingRequest?.status === 'pending';
  };

  useEffect(() => {
    if (id) {
      loadProblem();
      checkExistingRequest();
    } else {
      setError('No problem ID provided');
      setIsLoading(false);
    }
  }, [id]);

  const loadProblem = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data: problemData, error: problemError } = await supabase
        .from('problem_bank')
        .select('*')
        .eq('id', id)
        .single();

      if (problemError) throw problemError;

      if (!problemData) {
        setError('Problem not found');
        setIsLoading(false);
        return;
      }

      // Fetch verifier name if verified_by exists
      if (problemData.verified_by) {
        const { data: verifier } = await supabase
          .from('users')
          .select('name')
          .eq('id', problemData.verified_by)
          .single();
        if (verifier) {
          setVerifierName(verifier.name);
        }
      }

      const parsedProblem = {
        id: problemData.id,
        problemId: problemData.problem_id,
        title: problemData.title,
        status: problemData.status,
        dateDiscovered: problemData.date_discovered,
        source: problemData.source,
        category: problemData.category || [],
        industrySector: problemData.industry_sector,
        anchor: problemData.anchor ? (typeof problemData.anchor === 'string' ? JSON.parse(problemData.anchor) : problemData.anchor) : null,
        quantification: problemData.quantification ? (typeof problemData.quantification === 'string' ? JSON.parse(problemData.quantification) : problemData.quantification) : null,
        rootCauseAnalysis: problemData.root_cause_analysis ? (typeof problemData.root_cause_analysis === 'string' ? JSON.parse(problemData.root_cause_analysis) : problemData.root_cause_analysis) : null,
        solutionPotential: problemData.solution_potential ? (typeof problemData.solution_potential === 'string' ? JSON.parse(problemData.solution_potential) : problemData.solution_potential) : null,
        verification: problemData.verification ? (typeof problemData.verification === 'string' ? JSON.parse(problemData.verification) : problemData.verification) : null,
        priorityScore: problemData.priority_score ? (typeof problemData.priority_score === 'string' ? JSON.parse(problemData.priority_score) : problemData.priority_score) : null,
        assignment: problemData.assignment ? (typeof problemData.assignment === 'string' ? JSON.parse(problemData.assignment) : problemData.assignment) : null,
        outcome: problemData.outcome ? (typeof problemData.outcome === 'string' ? JSON.parse(problemData.outcome) : problemData.outcome) : null,
        createdBy: problemData.created_by,
        createdAt: problemData.created_at,
        updatedBy: problemData.updated_by,
        updatedAt: problemData.updated_at,
        verifiedBy: problemData.verified_by,
        verifiedAt: problemData.verified_at
      };

      setProblem(parsedProblem);
      
      if (parsedProblem.priorityScore) {
        setPriorityForm({
          lossMagnitude: parsedProblem.priorityScore.lossMagnitude?.score || 3,
          anchorUrgency: parsedProblem.priorityScore.anchorUrgency?.score || 3,
          solutionFeasibility: parsedProblem.priorityScore.solutionFeasibility?.score || 3,
          marketSize: parsedProblem.priorityScore.marketSize?.score || 3,
          relationshipStrength: parsedProblem.priorityScore.relationshipStrength?.score || 3,
          notes: ''
        });
      }
      
    } catch (error) {
      console.error('Failed to load problem:', error);
      setError(error instanceof Error ? error.message : 'Failed to load problem');
      toast({
        title: 'Error',
        description: 'Failed to load problem details',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const checkExistingRequest = async () => {
    if (!id) return;
    
    try {
      const { data } = await supabase
        .from('problem_deletion_requests')
        .select('*')
        .eq('problem_id', id)
        .eq('status', 'pending')
        .single();
      
      setExistingRequest(data);
    } catch (error) {
      // No pending request found
      setExistingRequest(null);
    }
  };

  const getPriorityColor = (tier: string) => {
    const colors: Record<string, string> = {
      critical: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[tier] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const calculateTotalScore = () => {
    const total = (
      (priorityForm.lossMagnitude * 0.30) +
      (priorityForm.anchorUrgency * 0.25) +
      (priorityForm.solutionFeasibility * 0.20) +
      (priorityForm.marketSize * 0.15) +
      (priorityForm.relationshipStrength * 0.10)
    );
    return total;
  };

  const getPriorityTier = (score: number) => {
    if (score >= 4.0) return 'critical';
    if (score >= 3.0) return 'high';
    if (score >= 2.0) return 'medium';
    return 'low';
  };

  const getLossMagnitudeScore = () => {
    const annualLoss = problem?.quantification?.totalLossPerYear || 0;
    if (annualLoss < 3000000) return 1;
    if (annualLoss < 6000000) return 2;
    if (annualLoss < 10000000) return 3;
    if (annualLoss < 15000000) return 4;
    return 5;
  };

  const getMarketSizeScore = () => {
    const marketSize = problem?.solutionPotential?.marketSizeKenya || 0;
    if (marketSize < 5) return 1;
    if (marketSize < 10) return 2;
    if (marketSize < 20) return 3;
    if (marketSize < 50) return 4;
    return 5;
  };

  const getUrgencyDescription = (score: number) => {
    const descriptions: Record<number, string> = {
      1: 'No urgency - not a priority',
      2: 'Low urgency - within 2+ years',
      3: 'Medium urgency - within the year',
      4: 'High urgency - within 6 months',
      5: 'Critical - needs solution now'
    };
    return descriptions[score] || 'Unknown';
  };

  const getFeasibilityDescription = (score: number) => {
    const descriptions: Record<number, string> = {
      1: 'Impossible - technology doesn\'t exist',
      2: 'Very difficult - significant R&D required',
      3: 'Possible - some R&D required',
      4: 'Likely - existing solutions can be adapted',
      5: 'Certain - solution exists'
    };
    return descriptions[score] || 'Unknown';
  };

  const getRelationshipDescription = (score: number) => {
    const descriptions: Record<number, string> = {
      1: 'Cold - first contact',
      2: 'Introductory - basic rapport',
      3: 'Engaged - multiple meetings, sharing data',
      4: 'Trusted - open communication, sharing sensitive data',
      5: 'Partner - committed relationship, prior success'
    };
    return descriptions[score] || 'Unknown';
  };

  const handleVerifyProblem = async () => {
    setIsVerifying(true);
    
    const totalScore = calculateTotalScore();
    const priorityTier = getPriorityTier(totalScore);
    const lossMagnitudeScore = getLossMagnitudeScore();
    const marketSizeScore = getMarketSizeScore();
    
    const priorityScore = {
      lossMagnitude: {
        score: lossMagnitudeScore,
        annualLoss: problem?.quantification?.totalLossPerYear || 0
      },
      anchorUrgency: {
        score: priorityForm.anchorUrgency,
        description: getUrgencyDescription(priorityForm.anchorUrgency)
      },
      solutionFeasibility: {
        score: priorityForm.solutionFeasibility,
        description: getFeasibilityDescription(priorityForm.solutionFeasibility)
      },
      marketSize: {
        score: marketSizeScore,
        customerCount: problem?.solutionPotential?.marketSizeKenya || 0
      },
      relationshipStrength: {
        score: priorityForm.relationshipStrength,
        description: getRelationshipDescription(priorityForm.relationshipStrength)
      },
      totalScore: totalScore,
      priorityTier: priorityTier,
      calculatedAt: new Date().toISOString(),
      calculatedBy: user?.id || null,
      notes: priorityForm.notes
    };
    
    try {
      const { error: updateError } = await supabase
        .from('problem_bank')
        .update({
          status: 'verified',
          priority_score: priorityScore,
          verified_by: user?.id,
          verified_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          updated_by: user?.id
        })
        .eq('id', problem.id);
      
      if (updateError) throw updateError;
      
      toast({
        title: 'Problem Verified',
        description: `${problem.title} has been verified and is now ready for assignment. Priority: ${priorityTier.toUpperCase()}`,
      });
      
      setVerifyDialogOpen(false);
      loadProblem();
      
    } catch (error) {
      console.error('Verification failed:', error);
      toast({
        title: 'Verification Failed',
        description: error instanceof Error ? error.message : 'Failed to verify problem',
        variant: 'destructive'
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleRequestDeletion = async () => {
    if (!problem || !user) return;
    
    setIsSubmittingRequest(true);
    try {
      // Create deletion request
      const { data: request, error: requestError } = await supabase
        .from('problem_deletion_requests')
        .insert({
          problem_id: problem.id,
          problem_title: problem.title,
          requested_by: user.id,
          requested_by_name: user.name,
          reason: deletionReason,
          status: 'pending'
        })
        .select()
        .single();
      
      if (requestError) throw requestError;
      
      toast({
        title: 'Deletion Request Submitted',
        description: 'Your request has been sent to Senior Analysts for review.',
      });
      
      setDeleteRequestDialogOpen(false);
      setDeletionReason('');
      checkExistingRequest();
      
    } catch (error) {
      console.error('Error submitting deletion request:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit deletion request',
        variant: 'destructive'
      });
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  const getUrgencyOptions = () => {
    return [1, 2, 3, 4, 5].map(score => ({
      value: score.toString(),
      label: `${score} - ${getUrgencyDescription(score)}`
    }));
  };

  const getFeasibilityOptions = () => {
    return [1, 2, 3, 4, 5].map(score => ({
      value: score.toString(),
      label: `${score} - ${getFeasibilityDescription(score)}`
    }));
  };

  const getRelationshipOptions = () => {
    return [1, 2, 3, 4, 5].map(score => ({
      value: score.toString(),
      label: `${score} - ${getRelationshipDescription(score)}`
    }));
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-full" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Skeleton className="h-64 w-full rounded-lg" />
        <div className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (error || !problem) {
    return (
      <div className="max-w-md mx-auto px-4 py-12">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-12 pb-12 text-center">
            <XCircle className="h-16 w-16 mx-auto mb-4 text-red-500" />
            <p className="text-xl font-semibold text-red-700 mb-2">Problem Not Found</p>
            <p className="text-sm text-red-600">{error || 'The problem you\'re looking for doesn\'t exist.'}</p>
            <Button variant="outline" className="mt-6" onClick={() => navigate('/problem-bank')}>
              Back to Problem Bank
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="h-auto p-0">
          Dashboard
        </Button>
        <ChevronRight className="h-3 w-3" />
        <Button variant="ghost" onClick={() => navigate('/problem-bank')} className="h-auto p-0">
          Problem Bank
        </Button>
        <ChevronRight className="h-3 w-3" />
        <span className="text-foreground font-medium">Problem Details</span>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">{problem.title}</h1>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge className={STATUS_COLORS[problem.status] || 'bg-gray-100'}>
              {problem.status?.toUpperCase() || 'UNKNOWN'}
            </Badge>
            {problem.priorityScore && (
              <Badge className={getPriorityColor(problem.priorityScore.priorityTier)}>
                {problem.priorityScore.priorityTier?.toUpperCase()} PRIORITY
              </Badge>
            )}
            <span className="text-muted-foreground flex items-center gap-1">
              <Hash className="h-4 w-4" />
              {problem.problemId}
            </span>
            <span className="text-muted-foreground flex items-center gap-1">
              <CalendarDays className="h-4 w-4" />
              Discovered: {problem.dateDiscovered ? new Date(problem.dateDiscovered).toLocaleDateString() : 'Unknown'}
            </span>
          </div>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          {/* Verify Button */}
          {canVerify() && canBeVerified() && (
            <Button 
              variant="default" 
              size="sm" 
              onClick={() => setVerifyDialogOpen(true)}
              className="bg-green-600 hover:bg-green-700"
            >
              <ShieldCheck className="h-4 w-4 mr-2" />
              Verify Problem
            </Button>
          )}
          
          {/* Delete Request Button */}
          {canRequestDeletion() && !isDeleted() && (
            <Button 
              variant="destructive" 
              size="sm" 
              onClick={() => setDeleteRequestDialogOpen(true)}
              className="bg-red-600 hover:bg-red-700"
              disabled={hasPendingRequest()}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {hasPendingRequest() ? 'Request Pending' : 'Request Deletion'}
            </Button>
          )}
          
          {problem.status === 'assigned' && (
            <Button 
              variant="default" 
              size="sm" 
              onClick={() => navigate(`/problem-bank/problem/${problem.id}/progress`)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Track Progress
            </Button>
          )}
          
          {problem.status === 'assigned' && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate(`/problem-bank/problem/${problem.id}/squads`)}
              className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
            >
              <Users className="h-4 w-4 mr-2" />
              Manage Squads
            </Button>
          )}
          
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Verified By Info - Show who verified this problem */}
      {problem.verified_by && (
        <Alert className="bg-green-50 border-green-200">
          <UserCheck className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-800">Verified by {verifierName || problem.verified_by}</AlertTitle>
          <AlertDescription className="text-green-700">
            This problem was verified on {new Date(problem.verified_at).toLocaleDateString()} at {new Date(problem.verified_at).toLocaleTimeString()}
          </AlertDescription>
        </Alert>
      )}

      {/* Pending Request Alert */}
      {hasPendingRequest() && (
        <Alert className="bg-yellow-50 border-yellow-200">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertTitle className="text-yellow-800">Deletion Request Pending</AlertTitle>
          <AlertDescription className="text-yellow-700">
            A deletion request for this problem has been submitted and is awaiting review by a Senior Analyst.
          </AlertDescription>
        </Alert>
      )}

      {/* Priority Score Banner */}
      {problem.priorityScore && (
        <Card className={`border-l-4 ${
          problem.priorityScore.priorityTier === 'critical' 
            ? 'border-l-red-500 bg-red-50' 
            : problem.priorityScore.priorityTier === 'high'
            ? 'border-l-orange-500 bg-orange-50'
            : 'border-l-yellow-500 bg-yellow-50'
        }`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm flex-wrap">
              <Gauge className="h-4 w-4 text-blue-600" />
              <span className="font-medium">Priority Score: {problem.priorityScore.totalScore?.toFixed(1) || 'N/A'}/5.0</span>
              <span className="text-muted-foreground">•</span>
              <span className="text-red-600 font-medium">{formatCurrency(problem.quantification?.totalLossPerYear || 0)}</span>
              <span className="text-muted-foreground">loss</span>
              <span className="text-muted-foreground">•</span>
              <span className="text-blue-600 font-medium">{problem.solutionPotential?.marketSizeKenya || 0} factories</span>
              <span className="text-muted-foreground">•</span>
              <span className="text-green-600 font-medium">{problem.solutionPotential?.anchorPaybackMonths || 0}mo</span>
              <span className="text-muted-foreground">payback</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Annual Loss</p>
            <p className="text-2xl font-bold text-red-600">{formatCurrency(problem.quantification?.totalLossPerYear || 0)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Market Size</p>
            <p className="text-2xl font-bold text-blue-600">{problem.solutionPotential?.marketSizeKenya || 0} factories</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Payback Period</p>
            <p className="text-2xl font-bold text-green-600">{problem.solutionPotential?.anchorPaybackMonths || 0} months</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Dev Cost</p>
            <p className="text-2xl font-bold text-purple-600">{formatCurrency(problem.solutionPotential?.estimatedDevCost || 0)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs - Keep existing tabs content unchanged */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="quantification">Quantification</TabsTrigger>
          <TabsTrigger value="root-cause">Root Cause</TabsTrigger>
          <TabsTrigger value="solution">Solution</TabsTrigger>
          <TabsTrigger value="evidence">Evidence</TabsTrigger>
        </TabsList>

        {/* All tab contents remain the same as before */}
        {/* ... (keep existing tab content) ... */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Anchor Company Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Anchor Company
                </CardTitle>
              </CardHeader>
              <CardContent>
                {problem.anchor ? (
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-lg">{problem.anchor.name}</h3>
                      <div className="space-y-1 mt-1">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="h-4 w-4" />
                          <span>{problem.anchor.location}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground pl-6">
                          <span>{problem.anchor.plantDepartment}</span>
                        </div>
                      </div>
                    </div>

                    <Badge variant="outline" className={
                      (problem.anchor.relationshipStrength || 0) >= 4 
                        ? 'bg-green-100 text-green-700 border-green-200'
                        : (problem.anchor.relationshipStrength || 0) >= 3
                        ? 'bg-blue-100 text-blue-700 border-blue-200'
                        : 'bg-gray-100 text-gray-700 border-gray-200'
                    }>
                      Relationship: {problem.anchor.relationshipStrength || 0}/5
                    </Badge>

                    <Separator />

                    {problem.anchor.contactPerson && (
                      <div className="space-y-3">
                        <div>
                          <p className="font-medium">{problem.anchor.contactPerson.name}</p>
                          <p className="text-xs text-muted-foreground">{problem.anchor.contactPerson.title}</p>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <a href={`mailto:${problem.anchor.contactPerson.email}`} className="hover:underline text-blue-600">
                              {problem.anchor.contactPerson.email}
                            </a>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            <a href={`tel:${problem.anchor.contactPerson.phone}`} className="hover:underline">
                              {problem.anchor.contactPerson.phone}
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No anchor company information available</p>
                )}
              </CardContent>
            </Card>

            {/* Priority Score Breakdown Card */}
            {problem.priorityScore && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Priority Score Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Loss Magnitude</span>
                        <span className="font-medium">{problem.priorityScore.lossMagnitude?.score || 0}/5</span>
                      </div>
                      <Progress value={(problem.priorityScore.lossMagnitude?.score || 0) * 20} className="h-2" />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Anchor Urgency</span>
                        <span className="font-medium">{problem.priorityScore.anchorUrgency?.score || 0}/5</span>
                      </div>
                      <Progress value={(problem.priorityScore.anchorUrgency?.score || 0) * 20} className="h-2" />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Solution Feasibility</span>
                        <span className="font-medium">{problem.priorityScore.solutionFeasibility?.score || 0}/5</span>
                      </div>
                      <Progress value={(problem.priorityScore.solutionFeasibility?.score || 0) * 20} className="h-2" />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Market Size</span>
                        <span className="font-medium">{problem.priorityScore.marketSize?.score || 0}/5</span>
                      </div>
                      <Progress value={(problem.priorityScore.marketSize?.score || 0) * 20} className="h-2" />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-muted-foreground">Relationship</span>
                        <span className="font-medium">{problem.priorityScore.relationshipStrength?.score || 0}/5</span>
                      </div>
                      <Progress value={(problem.priorityScore.relationshipStrength?.score || 0) * 20} className="h-2" />
                    </div>
                    <Separator className="my-2" />
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Total Score</span>
                      <Badge className={getPriorityColor(problem.priorityScore.priorityTier)}>
                        {problem.priorityScore.totalScore?.toFixed(1) || 0}/5.0
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Categories & Tags Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5" />
                Categories & Tags
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Categories</p>
                  <div className="flex flex-wrap gap-2">
                    {problem.category?.map((cat: string, i: number) => (
                      <Badge key={i} variant="secondary" className="px-3 py-1">
                        {cat.replace('_', ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
                <Separator orientation="vertical" className="h-auto" />
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Source</p>
                  <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                    <Code className="h-3 w-3 mr-1" />
                    {problem.source?.replace('_', ' ') || 'Unknown'}
                  </Badge>
                </div>
                <Separator orientation="vertical" className="h-auto" />
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Industry</p>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                    {problem.industrySector?.replace('_', ' ') || 'Other'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Full Problem Description */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5" />
                Problem Description
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-line text-sm leading-relaxed">
                {problem.quantification?.description || 'No description available'}
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quantification Tab - Keep existing */}
        <TabsContent value="quantification">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Loss Quantification
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-muted/30 p-4 rounded-lg border">
                <p className="whitespace-pre-line text-sm">
                  {problem.quantification?.calculationMethodology || 'Not available'}
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium mb-3">Data Sources</h4>
                  <ul className="space-y-2">
                    {problem.quantification?.dataSources?.map((source: string, i: number) => (
                      <li key={i} className="text-sm flex items-start gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <span>{source}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-3">Confidence Level</h4>
                  <Badge className={
                    problem.quantification?.confidenceLevel === 'high' 
                      ? 'bg-green-100 text-green-700 border-green-200 px-3 py-1'
                      : problem.quantification?.confidenceLevel === 'medium'
                      ? 'bg-yellow-100 text-yellow-700 border-yellow-200 px-3 py-1'
                      : 'bg-red-100 text-red-700 border-red-200 px-3 py-1'
                  }>
                    {problem.quantification?.confidenceLevel?.toUpperCase() || 'LOW'}
                  </Badge>
                  
                  <h4 className="text-sm font-medium mt-6 mb-3">Assumptions</h4>
                  <div className="space-y-3">
                    {problem.quantification?.assumptions?.map((assumption: any, i: number) => (
                      <div key={i} className="text-sm p-3 bg-muted/30 rounded-lg border">
                        <p className="font-medium">{assumption.assumption}: {assumption.value}</p>
                        <p className="text-xs text-muted-foreground mt-1">{assumption.justification}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Root Cause Tab - Keep existing */}
        <TabsContent value="root-cause">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Root Cause Analysis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 mb-3">
                  {problem.rootCauseAnalysis?.rootCauseCategory || 'Unknown'}
                </Badge>
                <p className="text-sm leading-relaxed">{problem.rootCauseAnalysis?.rootCauseDetails || 'Not available'}</p>
              </div>
              
              <Separator />
              
              <div>
                <h4 className="text-sm font-medium mb-3">5 Whys Analysis</h4>
                <div className="space-y-3">
                  {problem.rootCauseAnalysis?.fiveWhys?.map((why: any, i: number) => (
                    <div key={i} className="pl-4 border-l-4 border-blue-200">
                      <p className="text-xs font-medium text-blue-600">Why {why.level}?</p>
                      <p className="text-sm mt-1">{why.answer}</p>
                    </div>
                  ))}
                </div>
              </div>

              {problem.rootCauseAnalysis?.barriersToInternalSolution && (
                <>
                  <Separator />
                  <div>
                    <h4 className="text-sm font-medium mb-3">Barriers to Internal Solution</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {problem.rootCauseAnalysis.barriersToInternalSolution.map((barrier: string, i: number) => (
                        <li key={i} className="text-sm">{barrier}</li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Solution Tab - Keep existing */}
        <TabsContent value="solution">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Solution Hypothesis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm leading-relaxed whitespace-pre-line">
                {problem.solutionPotential?.hypothesis || 'Not available'}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium mb-3">Key Features</h4>
                  <ul className="space-y-2">
                    {problem.solutionPotential?.keyFeatures?.map((feature: string, i: number) => (
                      <li key={i} className="text-sm flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-3">MVP Features</h4>
                  <ul className="space-y-2">
                    {problem.solutionPotential?.mvpFeatures?.map((feature: string, i: number) => (
                      <li key={i} className="text-sm flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <Separator />

              <div>
                <h4 className="text-sm font-medium mb-3">Pricing Model</h4>
                <p className="text-sm p-3 bg-muted/30 rounded-lg border">
                  {problem.solutionPotential?.estimatedPriceModel || 'Not specified'}
                </p>
              </div>

              {problem.solutionPotential?.competitorLandscape && (
                <div>
                  <h4 className="text-sm font-medium mb-3">Competitor Landscape</h4>
                  <p className="text-sm p-3 bg-muted/30 rounded-lg border whitespace-pre-line">
                    {problem.solutionPotential.competitorLandscape}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Evidence Tab - Keep existing */}
        <TabsContent value="evidence">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Evidence Files
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Photos & Videos */}
              <div>
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Camera className="h-4 w-4" />
                  Photos & Videos ({problem.verification?.photoVideoEvidence?.length || 0})
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {problem.verification?.photoVideoEvidence?.map((url: string, i: number) => (
                    <a
                      key={i}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-3 border rounded-lg hover:bg-muted transition-colors"
                    >
                      {url.endsWith('.mp4') ? (
                        <Video className="h-4 w-4 text-blue-600" />
                      ) : (
                        <Image className="h-4 w-4 text-green-600" />
                      )}
                      <span className="text-sm truncate flex-1">Evidence {i + 1}</span>
                      <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    </a>
                  ))}
                </div>
              </div>

              {/* Data Files */}
              <div>
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4" />
                  Data Files ({problem.verification?.dataFilesReceived?.length || 0})
                </h4>
                <div className="space-y-2">
                  {problem.verification?.dataFilesReceived?.map((file: any, i: number) => (
                    <a
                      key={i}
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted transition-colors"
                    >
                      <FileText className="h-4 w-4 text-purple-600" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{file.type}</p>
                        {file.description && (
                          <p className="text-xs text-muted-foreground">{file.description}</p>
                        )}
                      </div>
                      <ExternalLink className="h-3 w-3 text-muted-foreground" />
                    </a>
                  ))}
                </div>
              </div>

              {/* Anchor Acknowledgment */}
              <div className="bg-muted/30 p-4 rounded-lg border">
                <h4 className="text-sm font-medium mb-3">Anchor Acknowledgment</h4>
                {problem.verification?.anchorAcknowledgment?.obtained ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm">
                      Confirmed via {problem.verification.anchorAcknowledgment.method} on{' '}
                      {problem.verification.anchorAcknowledgment.date ? new Date(problem.verification.anchorAcknowledgment.date).toLocaleDateString() : 'Unknown'}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-yellow-600">
                    <AlertTriangle className="h-4 w-4" />
                    <span className="text-sm">Pending anchor acknowledgment</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Verify Problem Dialog */}
      <Dialog open={verifyDialogOpen} onOpenChange={setVerifyDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Verify Problem</DialogTitle>
            <DialogDescription>
              Review the problem details and set priority scores before marking it as verified.
              Once verified, this problem will appear in the "Ready to Assign" section.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="bg-muted/30 p-4 rounded-lg">
              <h4 className="font-medium">{problem.title}</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Annual Loss: {formatCurrency(problem.quantification?.totalLossPerYear || 0)}
              </p>
              <p className="text-sm text-muted-foreground">
                Market Size: {problem.solutionPotential?.marketSizeKenya || 0} potential customers
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium">Auto-calculated Scores</Label>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-2 bg-muted/20 rounded">
                  <span className="text-muted-foreground">Loss Magnitude:</span>
                  <span className="ml-2 font-medium">{getLossMagnitudeScore()}/5</span>
                </div>
                <div className="p-2 bg-muted/20 rounded">
                  <span className="text-muted-foreground">Market Size:</span>
                  <span className="ml-2 font-medium">{getMarketSizeScore()}/5</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <Label className="text-sm font-medium">Scores to Set Manually</Label>
              
              <div className="space-y-2">
                <Label>Anchor Urgency</Label>
                <Select 
                  value={priorityForm.anchorUrgency.toString()} 
                  onValueChange={(v) => setPriorityForm({...priorityForm, anchorUrgency: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select urgency level" />
                  </SelectTrigger>
                  <SelectContent>
                    {getUrgencyOptions().map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Solution Feasibility</Label>
                <Select 
                  value={priorityForm.solutionFeasibility.toString()} 
                  onValueChange={(v) => setPriorityForm({...priorityForm, solutionFeasibility: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select feasibility level" />
                  </SelectTrigger>
                  <SelectContent>
                    {getFeasibilityOptions().map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Relationship Strength</Label>
                <Select 
                  value={priorityForm.relationshipStrength.toString()} 
                  onValueChange={(v) => setPriorityForm({...priorityForm, relationshipStrength: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select relationship strength" />
                  </SelectTrigger>
                  <SelectContent>
                    {getRelationshipOptions().map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2">Priority Score Preview</h4>
              <div className="flex justify-between items-center">
                <span className="text-blue-700">Total Score:</span>
                <Badge className={getPriorityColor(getPriorityTier(calculateTotalScore()))}>
                  {calculateTotalScore().toFixed(1)}/5.0 - {getPriorityTier(calculateTotalScore()).toUpperCase()}
                </Badge>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Verification Notes (Optional)</Label>
              <Textarea 
                placeholder="Add any notes about the verification process..."
                value={priorityForm.notes}
                onChange={(e) => setPriorityForm({...priorityForm, notes: e.target.value})}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerifyDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleVerifyProblem} 
              disabled={isVerifying}
              className="bg-green-600 hover:bg-green-700"
            >
              {isVerifying ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verifying...</>
              ) : (
                <><ShieldCheck className="mr-2 h-4 w-4" />Verify Problem</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Request Dialog */}
      <Dialog open={deleteRequestDialogOpen} onOpenChange={setDeleteRequestDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Problem Deletion</DialogTitle>
            <DialogDescription>
              Please provide a reason for deleting this problem. A Senior Analyst will review your request.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-muted/30 p-4 rounded-lg">
              <p className="font-medium">{problem?.title}</p>
              <p className="text-sm text-muted-foreground mt-1">
                Problem ID: {problem?.problemId}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Reason for Deletion *</Label>
              <Textarea
                value={deletionReason}
                onChange={(e) => setDeletionReason(e.target.value)}
                placeholder="Explain why this problem should be deleted..."
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteRequestDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRequestDeletion} 
              disabled={!deletionReason.trim() || isSubmittingRequest}
              className="bg-red-600 hover:bg-red-700"
            >
              {isSubmittingRequest ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Submit Deletion Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}